import { useEffect, useState, type ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../../shared/components/ui/badge";
import {
  StatusBanner,
  SubmissionContent,
  WithdrawDialog,
} from "../../content-editing/components/ViewSubmissionComponents";
import { useSubmissionMutations } from "../../content-editing/hooks/useSubmissionMutations";
import { useSubmission } from "../../content-editing/hooks/useSubmissions";

/**
 * Read-only View for PENDING/APPROVED/PUBLISHED submissions
 * with Withdraw option for PENDING
 */
export function ViewSubmission(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: submission, loading: loadingSubmission, error: loadError } = useSubmission(id || "");
  const { withdrawSubmission, loading: withdrawLoading } = useSubmissionMutations();
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");

  useEffect(() => {
    if (submission && (submission.status === "DRAFT" || submission.status === "REJECTED")) {
      // If it's editable, redirect to edit page
      navigate(`/provider/edit/${id}`);
    }
  }, [submission, id, navigate]);

  const handleWithdraw = async () => {
    if (!id) return;

    try {
      await withdrawSubmission(id, withdrawReason);
      // Redirect to dashboard after successful withdrawal
      navigate("/provider");
    } catch (error) {
      console.error("Failed to withdraw submission:", error);
      alert(`Failed to withdraw submission: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const getStatusVariant = (status: string): "draft" | "pending" | "approved" | "rejected" | "published" => {
    const variants: Record<string, "draft" | "pending" | "approved" | "rejected" | "published"> = {
      DRAFT: "draft",
      PENDING: "pending",
      APPROVED: "approved",
      REJECTED: "rejected",
      PUBLISHED: "published",
    };
    return variants[status.toUpperCase()] || "draft";
  };

  if (loadingSubmission) {
    return (
      <div className="mx-auto py-8 px-4">
        <div className="text-center text-neutral-400">Loading submission...</div>
      </div>
    );
  }

  if (loadError || !submission) {
    return (
      <div className="mx-auto py-8 px-4">
        <div className="text-center text-red-400">
          {loadError instanceof Error ? loadError.message : (loadError ?? "Submission not found")}
        </div>
        <div className="text-center mt-4">
          <Link to="/provider" className="text-lumi-accent hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-6">
        <Link to="/provider" className="text-neutral-400 hover:text-white mb-4 inline-block">
          ← Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Submission Details</h1>
          <Badge variant={getStatusVariant(submission.status)}>{submission.status}</Badge>
        </div>
      </div>

      <StatusBanner status={submission.status} />
      <SubmissionContent submission={submission} />

      {/* Actions */}
      <div className="mt-6 flex items-center justify-between">
        <Link to="/provider" className="px-6 py-2 text-neutral-400 hover:text-white transition-colors">
          ← Back to Dashboard
        </Link>

        {submission.status === "PENDING" && (
          <button
            onClick={() => setShowWithdrawDialog(true)}
            disabled={withdrawLoading}
            className="px-6 py-2 bg-red-600/10 border border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-600/100 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Withdraw Submission
          </button>
        )}
      </div>

      <WithdrawDialog
        isOpen={showWithdrawDialog}
        loading={withdrawLoading}
        reason={withdrawReason}
        onReasonChange={setWithdrawReason}
        onConfirm={() => void handleWithdraw()}
        onCancel={() => {
          setShowWithdrawDialog(false);
          setWithdrawReason("");
        }}
      />
    </div>
  );
}
